import React, { useState } from "react";

const S = {
  wrapper: {
    marginTop: '10px', marginLeft: '0px', border: '3px', padding: '40px',
    borderStyle: 'solid', borderColor: 'grey', fontFamily: "'Georgia', serif",
    color: '#1a1a2e', lineHeight: '1.7',
  },
  h1: {
    fontSize: '2rem', fontWeight: '700', borderBottom: '3px solid #2E75B6',
    paddingBottom: '8px', marginBottom: '24px', color: '#1F3864',
  },
  h2: {
    fontSize: '1.3rem', fontWeight: '700', color: '#2E75B6', marginTop: '36px',
    marginBottom: '12px', borderLeft: '4px solid #2E75B6', paddingLeft: '10px',
  },
  p: { marginBottom: '12px', fontSize: '0.97rem' },
  infoBox: {
    background: '#EBF3FB', border: '2px solid #2E75B6', borderRadius: '6px',
    padding: '16px 20px', margin: '16px 0',
  },
  infoBoxTitle: {
    fontWeight: '700', fontFamily: "'Courier New', monospace", fontSize: '0.88rem',
    color: '#1F3864', marginBottom: '8px',
  },
  infoBoxItem: {
    fontSize: '0.9rem', margin: '4px 0', paddingLeft: '12px', borderLeft: '2px solid #2E75B6',
  },
  table: { width: '100%', borderCollapse: 'collapse', marginBottom: '20px', fontSize: '0.92rem' },
  th: { background: '#2E75B6', color: '#fff', padding: '10px 14px', textAlign: 'left', fontWeight: '700', fontSize: '0.9rem' },
  tdE: { padding: '10px 14px', borderBottom: '1px solid #ccd9ea', verticalAlign: 'top', background: '#f5f9ff' },
  tdO: { padding: '10px 14px', borderBottom: '1px solid #ccd9ea', verticalAlign: 'top', background: '#fff' },
  tdL: { fontWeight: '600', color: '#1F3864', whiteSpace: 'nowrap', width: '220px' },
  code: { fontFamily: "'Courier New', monospace", fontSize: '0.88rem', whiteSpace: 'pre-wrap' },
  dWrap: {
    margin: '24px 0', background: '#f8fbff', border: '1px solid #ccd9ea',
    borderRadius: '8px', padding: '20px',
  },
  dTitle: {
    fontWeight: '700', fontSize: '0.95rem', color: '#1F3864',
    marginBottom: '12px', textAlign: 'center', letterSpacing: '0.02em',
  },
  dCaption: {
    fontSize: '0.84rem', color: '#555', textAlign: 'center', marginTop: '10px', fontStyle: 'italic',
  },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width: '220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i % 2 === 0 ? S.tdE : S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i % 2 === 0 ? S.tdE : S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Grid({ ox, oy, sc, xTicks, yTicks, W, H }) {
  return (
    <g>
      {xTicks.map(v => v !== 0 && (
        <g key={v}>
          <line x1={ox+v*sc} y1={10} x2={ox+v*sc} y2={H-10} stroke="#eee" strokeWidth="1"/>
          <text x={ox+v*sc} y={oy+13} textAnchor="middle" fontSize="9" fill="#bbb">{v}</text>
        </g>
      ))}
      {yTicks.map(v => v !== 0 && (
        <g key={v}>
          <line x1={10} y1={oy-v*sc} x2={W-10} y2={oy-v*sc} stroke="#eee" strokeWidth="1"/>
          <text x={ox-5} y={oy-v*sc+3} textAnchor="end" fontSize="9" fill="#bbb">{v}</text>
        </g>
      ))}
      <line x1={10} y1={oy} x2={W-10} y2={oy} stroke="#1F3864" strokeWidth="1.5"/>
      <line x1={ox} y1={H-10} x2={ox} y2={10} stroke="#1F3864" strokeWidth="1.5"/>
      <text x={W-8} y={oy-4} fontSize="11" fill="#1F3864" fontWeight="700">x</text>
      <text x={ox+4} y={14} fontSize="11" fill="#1F3864" fontWeight="700">y</text>
    </g>
  );
}

function makePath(fn, xMin, xMax, toX, toY, H, step) {
  const s = step || 0.05;
  const segs = [], cur = [];
  for (let x = xMin; x <= xMax; x += s) {
    const y = fn(x);
    const sy = toY(y);
    if (!isFinite(y) || sy < 5 || sy > H - 5) {
      if (cur.length > 1) segs.push('M ' + cur.join(' L '));
      cur.length = 0;
    } else {
      cur.push(toX(x).toFixed(1) + ',' + sy.toFixed(1));
    }
  }
  if (cur.length > 1) segs.push('M ' + cur.join(' L '));
  return segs.join(' ');
}

function DiagramPolynomialAnatomy() {
  const W = 530, H = 270, ox = 230, oy = 160, sc = 50;
  const toX = x => ox + x * sc;
  const toY = y => oy - y * sc;
  const f = x => 0.3*x*x*x - 0.6*x*x - 1.2*x + 1;
  const path = makePath(f, -2.4, 3.8, toX, toY, H);
  const roots = [-1.55, 0.72, 3.03];
  const tp1x = -0.63, tp2x = 2.23;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Polynomial Anatomy — f(x) = 0.3x^3 - 0.6x^2 - 1.2x + 1</div>
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={H} viewBox={"0 0 " + W + " " + H} style={{ maxWidth: '100%' }}>
          <Grid ox={ox} oy={oy} sc={sc} xTicks={[-2,-1,0,1,2,3]} yTicks={[-2,-1,0,1,2]} W={W} H={H}/>
          <path d={path} fill="none" stroke="#2E75B6" strokeWidth="2.6" strokeLinejoin="round"/>
          {roots.map((r, i) => (
            <g key={i}>
              <circle cx={toX(r)} cy={oy} r={5} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>
              <text x={toX(r)} y={oy+18} textAnchor="middle" fontSize="9" fill="#c0392b" fontWeight="700">root</text>
            </g>
          ))}
          <circle cx={toX(tp1x)} cy={toY(f(tp1x))} r={5} fill="#27ae60" stroke="#fff" strokeWidth="1.5"/>
          <rect x={toX(tp1x)-78} y={toY(f(tp1x))-28} width={72} height={24} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
          <text x={toX(tp1x)-42} y={toY(f(tp1x))-14} textAnchor="middle" fontSize="10" fill="#27ae60" fontWeight="700">local max</text>
          <text x={toX(tp1x)-42} y={toY(f(tp1x))-3}  textAnchor="middle" fontSize="9"  fill="#27ae60">f'(x) = 0 here</text>
          <circle cx={toX(tp2x)} cy={toY(f(tp2x))} r={5} fill="#e07b00" stroke="#fff" strokeWidth="1.5"/>
          <rect x={toX(tp2x)+8} y={toY(f(tp2x))-2} width={68} height={24} rx={3} fill="#fff7ee" stroke="#e07b00" strokeWidth="1"/>
          <text x={toX(tp2x)+42} y={toY(f(tp2x))+12} textAnchor="middle" fontSize="10" fill="#e07b00" fontWeight="700">local min</text>
          <text x={toX(tp2x)+42} y={toY(f(tp2x))+23} textAnchor="middle" fontSize="9"  fill="#e07b00">f'(x) = 0 here</text>
          <text x={toX(-2.2)} y={toY(f(-2.2))-10} fontSize="10" fill="#2E75B6">as x to -inf, y to -inf</text>
          <text x={toX(3.3)}  y={toY(f(3.3))-10}  fontSize="10" fill="#2E75B6">as x to +inf, y to +inf</text>
          <rect x={W-195} y={12} width={186} height={108} rx={5} fill="#fff" stroke="#ccd9ea"/>
          <text x={W-102} y={28} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">Term breakdown</text>
          {[['0.3x^3','leading term - sets end behaviour','#2E75B6'],
            ['-0.6x^2','bends the curve','#555'],
            ['-1.2x','tilts the curve','#555'],
            ['+1','y-intercept = 1','#27ae60']
          ].map(([term,desc,col],i) => (
            <g key={i}>
              <text x={W-186} y={44+i*18} fontSize="10" fill={col} fontFamily="'Courier New',monospace" fontWeight="700">{term}</text>
              <text x={W-140} y={44+i*18} fontSize="10" fill="#555">{desc}</text>
            </g>
          ))}
          <rect x={W-195} y={122} width={186} height={36} rx={5} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
          <text x={W-102} y={136} textAnchor="middle" fontSize="10" fill="#1F3864" fontWeight="700">Degree 3: max 3 roots,</text>
          <text x={W-102} y={150} textAnchor="middle" fontSize="10" fill="#1F3864">2 turning points, odd symmetry</text>
        </svg>
      </div>
      <div style={S.dCaption}>A degree-3 polynomial has at most 3 roots and 2 turning points. The leading term controls end behaviour. Each root is where the graph crosses the x-axis.</div>
    </div>
  );
}

function DiagramEvenOdd() {
  const sc = 46, H = 220;
  const evenFn = x => 0.4*x*x - 0.8;
  const oddFn  = x => 0.3*x*x*x - 0.9*x;

  function Panel({ fn, color, label, sub, examples, sym, symLabel }) {
    const W2 = 240, ox2 = 120, oy2 = 120;
    const tX = x => ox2 + x*sc, tY = y => oy2 - y*sc;
    const path = makePath(fn, -2.8, 2.8, tX, tY, H);
    return (
      <div style={{ flex:'1', minWidth:'220px', background:'#fff', border:'2px solid '+color, borderRadius:'8px', padding:'12px', textAlign:'center' }}>
        <div style={{ fontWeight:'700', color, marginBottom:'2px' }}>{label}</div>
        <div style={{ fontFamily:"'Courier New',monospace", fontSize:'0.82rem', color:'#555', marginBottom:'4px' }}>{sub}</div>
        <svg width={W2} height={H} viewBox={"0 0 "+W2+" "+H} style={{ maxWidth:'100%' }}>
          {[-2,-1,0,1,2].map(v => <line key={v} x1={tX(v)} y1={10} x2={tX(v)} y2={H-10} stroke="#eee" strokeWidth="1"/>)}
          {[-1,0,1,2].map(v => <line key={v} x1={10} y1={tY(v)} x2={W2-10} y2={tY(v)} stroke="#eee" strokeWidth="1"/>)}
          <line x1={10} y1={oy2} x2={W2-10} y2={oy2} stroke="#1F3864" strokeWidth="1.4"/>
          <line x1={ox2} y1={H-10} x2={ox2} y2={10} stroke="#1F3864" strokeWidth="1.4"/>
          <path d={path} fill="none" stroke={color} strokeWidth="2.4"/>
          <circle cx={tX(sym)}  cy={tY(fn(sym))}  r={6} fill={color} stroke="#fff" strokeWidth="1.5"/>
          <circle cx={tX(-sym)} cy={tY(fn(-sym))} r={6} fill={color} stroke="#fff" strokeWidth="1.5"/>
          <line x1={tX(sym)} y1={tY(fn(sym))} x2={tX(-sym)} y2={tY(fn(-sym))} stroke="#e07b00" strokeWidth="1.3" strokeDasharray="4,3"/>
          <text x={tX(sym)+5}  y={tY(fn(sym))-6}  fontSize="10" fill={color} fontWeight="700">(x, y)</text>
          <text x={tX(-sym)-5} y={tY(fn(-sym))-6} textAnchor="end" fontSize="10" fill={color} fontWeight="700">{symLabel}</text>
          <text x={ox2} y={H-4} textAnchor="middle" fontSize="9" fill="#e07b00">{examples}</text>
        </svg>
      </div>
    );
  }

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Even vs Odd Functions — Symmetry About y-axis vs Origin</div>
      <div style={{ display:'flex', gap:'16px', flexWrap:'wrap', justifyContent:'center' }}>
        <Panel fn={evenFn} color="#2E75B6" label="Even Function" sub="f(-x) = f(x) — symmetric about y-axis"
          examples="same y at +x and -x  |  ex: x^2, cos(x)" sym={1.8} symLabel="(-x, y) same height"/>
        <Panel fn={oddFn}  color="#c0392b" label="Odd Function"  sub="f(-x) = -f(x) — symmetric about origin"
          examples="180 deg rotational symmetry  |  ex: x^3, sin(x)" sym={1.5} symLabel="(-x, -y) opposite"/>
      </div>
      <div style={S.dCaption}>Even functions fold onto themselves across the y-axis. Odd functions rotate 180° about the origin. Recognising symmetry saves work — odd function integrals over symmetric intervals equal zero.</div>
    </div>
  );
}

function DiagramComplexPlane() {
  const W = 520, H = 280, ox = 200, oy = 160, sc = 52;
  const toX = x => ox + x * sc;
  const toY = y => oy - y * sc;
  const re = 3, im = 2;
  const mag = Math.sqrt(re*re + im*im);
  const ang = Math.atan2(im, re);

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Complex Numbers on the Complex Plane — z = 3 + 2i</div>
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={H} viewBox={"0 0 "+W+" "+H} style={{ maxWidth:'100%' }}>
          <defs>
            <marker id="cArr" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto"><polygon points="0 0,7 3.5,0 7" fill="#2E75B6"/></marker>
            <marker id="cArrR" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto"><polygon points="0 0,7 3.5,0 7" fill="#9b59b6"/></marker>
          </defs>
          {[-3,-2,-1,0,1,2,3,4].map(v => <line key={v} x1={toX(v)} y1={10} x2={toX(v)} y2={H-10} stroke="#eee" strokeWidth="1"/>)}
          {[-2,-1,0,1,2,3].map(v => <line key={v} x1={10} y1={toY(v)} x2={W-20} y2={toY(v)} stroke="#eee" strokeWidth="1"/>)}
          <line x1={10} y1={oy} x2={W-14} y2={oy} stroke="#1F3864" strokeWidth="1.5"/>
          <line x1={ox} y1={H-10} x2={ox} y2={10} stroke="#1F3864" strokeWidth="1.5"/>
          <text x={W-10} y={oy+4} fontSize="11" fill="#1F3864" fontWeight="700">Re</text>
          <text x={ox+4} y={13}   fontSize="11" fill="#1F3864" fontWeight="700">Im</text>
          {[-3,-2,-1,1,2,3,4].map(v => <text key={v} x={toX(v)} y={oy+14} textAnchor="middle" fontSize="9" fill="#bbb">{v}</text>)}
          {[-2,-1,1,2,3].map(v => <text key={v} x={ox-6} y={toY(v)+3} textAnchor="end" fontSize="9" fill="#bbb">{v}i</text>)}
          <line x1={ox} y1={oy} x2={toX(re)-5} y2={toY(im)+3} stroke="#2E75B6" strokeWidth="2.5" markerEnd="url(#cArr)"/>
          <circle cx={toX(re)} cy={toY(im)} r={6} fill="#2E75B6" stroke="#fff" strokeWidth="2"/>
          <rect x={toX(re)+10} y={toY(im)-22} width={82} height={36} rx={4} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.2"/>
          <text x={toX(re)+51} y={toY(im)-8}  textAnchor="middle" fontSize="11" fill="#2E75B6" fontWeight="700">z = 3 + 2i</text>
          <text x={toX(re)+51} y={toY(im)+6}  textAnchor="middle" fontSize="9"  fill="#555">a=3, b=2</text>
          <line x1={ox} y1={toY(im)} x2={toX(re)} y2={toY(im)} stroke="#27ae60" strokeWidth="2" strokeLinecap="round"/>
          <text x={toX(re/2)} y={toY(im)+14} textAnchor="middle" fontSize="10" fill="#27ae60" fontWeight="700">Re = 3</text>
          <line x1={toX(re)} y1={oy} x2={toX(re)} y2={toY(im)} stroke="#c0392b" strokeWidth="2" strokeLinecap="round"/>
          <text x={toX(re)+14} y={toY(im/2)} fontSize="10" fill="#c0392b" fontWeight="700">Im = 2i</text>
          <circle cx={toX(re)} cy={toY(-im)} r={5} fill="#9b59b6" stroke="#fff" strokeWidth="1.5"/>
          <line x1={ox} y1={oy} x2={toX(re)-4} y2={toY(-im)-3} stroke="#9b59b6" strokeWidth="1.5" strokeDasharray="5,3" markerEnd="url(#cArrR)"/>
          <text x={toX(re)+8} y={toY(-im)+4} fontSize="10" fill="#9b59b6" fontWeight="700">z* = 3 - 2i (conjugate)</text>
          {(() => {
            const r = mag * sc;
            const x2c = ox + r*Math.cos(ang), y2c = oy - r*Math.sin(ang);
            return (
              <g>
                <path d={"M "+(ox+r)+","+oy+" A "+r+" "+r+" 0 0 0 "+x2c+","+y2c} fill="none" stroke="#e07b00" strokeWidth="1.3" strokeDasharray="4,3"/>
                <text x={ox+r*0.7+5} y={oy-12} fontSize="10" fill="#e07b00" fontWeight="700">{"|z| = "+mag.toFixed(2)}</text>
                <text x={ox+32} y={oy-9} fontSize="9" fill="#e07b00">{"theta = "+(ang*180/Math.PI).toFixed(1)+"deg"}</text>
              </g>
            );
          })()}
          <rect x={W-190} y={14} width={182} height={130} rx={5} fill="#fff" stroke="#ccd9ea"/>
          <text x={W-99} y={30} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">Forms of a Complex Number</text>
          {[['Rectangular:','a + bi','#2E75B6'],['Polar:','r(cos t + i sin t)','#e07b00'],['Euler:','r * e^(it)','#c0392b'],['Magnitude:','|z| = sqrt(a^2+b^2)','#27ae60'],['Angle:','t = arctan(b/a)','#27ae60'],['Conjugate:','z* = a - bi','#9b59b6']].map(([lbl,val,col],i) => (
            <g key={i}>
              <text x={W-182} y={46+i*16} fontSize="9" fill="#888" fontFamily="'Courier New',monospace">{lbl}</text>
              <text x={W-122} y={46+i*16} fontSize="9" fill={col} fontFamily="'Courier New',monospace" fontWeight="700">{val}</text>
            </g>
          ))}
        </svg>
      </div>
      <div style={S.dCaption}>A complex number a+bi is a 2D vector — real axis horizontal, imaginary vertical. Magnitude is |z|=sqrt(a^2+b^2), angle is theta=arctan(b/a). The conjugate reflects across the real axis and z*z = |z|^2.</div>
    </div>
  );
}

function DiagramRational() {
  const W = 520, H = 270, ox = 200, oy = 150, sc = 48;
  const toX = x => ox + x * sc;
  const toY = y => oy - y * sc;
  const f = x => (x+1) / ((x-2)*(x+3));
  const path = makePath(f, -5.5, 4.5, toX, toY, H, 0.02);

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Rational Expressions — Asymptotes and Behaviour near Singularities</div>
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={H} viewBox={"0 0 "+W+" "+H} style={{ maxWidth:'100%' }}>
          <Grid ox={ox} oy={oy} sc={sc} xTicks={[-5,-4,-3,-2,-1,0,1,2,3,4]} yTicks={[-2,-1,0,1,2]} W={W} H={H}/>
          {[2,-3].map(va => (
            <g key={va}>
              <line x1={toX(va)} y1={10} x2={toX(va)} y2={H-10} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="6,4"/>
              <rect x={toX(va)-(va>0?62:2)} y={H-32} width={60} height={20} rx={3} fill="#fff0f0" stroke="#c0392b" strokeWidth="1"/>
              <text x={toX(va)-(va>0?32:-28)} y={H-19} textAnchor="middle" fontSize="10" fill="#c0392b" fontWeight="700">{"VA: x = "+va}</text>
            </g>
          ))}
          <line x1={10} y1={oy} x2={W-10} y2={oy} stroke="#e07b00" strokeWidth="1.5" strokeDasharray="6,4" opacity="0.6"/>
          <rect x={W-170} y={oy-20} width={162} height={16} rx={3} fill="#fff7ee" stroke="#e07b00" strokeWidth="1"/>
          <text x={W-89} y={oy-8} textAnchor="middle" fontSize="10" fill="#e07b00" fontWeight="700">HA: y=0  (top deg less than bot deg)</text>
          <path d={path} fill="none" stroke="#2E75B6" strokeWidth="2.3" strokeLinejoin="round"/>
          <circle cx={toX(-1)} cy={oy} r={5} fill="#27ae60" stroke="#fff" strokeWidth="1.5"/>
          <text x={toX(-1)} y={oy-10} textAnchor="middle" fontSize="10" fill="#27ae60" fontWeight="700">x=-1 (zero)</text>
          <rect x={10} y={12} width={190} height={36} rx={4} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.2"/>
          <text x={105} y={27} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">f(x) = (x+1) / ((x-2)(x+3))</text>
          <text x={105} y={42} textAnchor="middle" fontSize="10" fill="#555" fontFamily="'Courier New',monospace">numerator deg 1,  denominator deg 2</text>
        </svg>
      </div>
      <div style={S.dCaption}>Vertical asymptotes occur where the denominator is zero. The curve approaches but never crosses them. Horizontal asymptotes describe long-run behaviour — degree comparison between numerator and denominator determines the value.</div>
    </div>
  );
}

function DiagramFactoring() {
  const W = 520, H = 260, ox = 200, oy = 180, sc = 52;
  const toX = x => ox + x * sc;
  const toY = y => oy - y * sc;
  const roots = [-2, 1, 3];
  const f = x => (x+2)*(x-1)*(x-3);
  const path = makePath(f, -2.8, 3.8, toX, toY, H);

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Factoring and Roots — f(x) = (x + 2)(x - 1)(x - 3)</div>
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={H} viewBox={"0 0 "+W+" "+H} style={{ maxWidth:'100%' }}>
          <Grid ox={ox} oy={oy} sc={sc} xTicks={[-2,-1,0,1,2,3]} yTicks={[-4,-2,0,2,4,6]} W={W} H={H}/>
          <path d={path} fill="none" stroke="#2E75B6" strokeWidth="2.6" strokeLinejoin="round"/>
          {roots.map((r, i) => {
            const colors = ['#c0392b','#27ae60','#e07b00'];
            const labels = ['(x + 2)','(x - 1)','(x - 3)'];
            const col = colors[i];
            return (
              <g key={i}>
                <line x1={toX(r)} y1={oy-2} x2={toX(r)} y2={oy+2} stroke={col} strokeWidth="2.5" strokeLinecap="round"/>
                <line x1={toX(r)} y1={oy-2} x2={toX(r)} y2={oy-34} stroke={col} strokeWidth="1.2" strokeDasharray="3,3"/>
                <rect x={toX(r)-32} y={oy-52} width={64} height={18} rx={4} fill="#fff" stroke={col} strokeWidth="1.5"/>
                <text x={toX(r)} y={oy-40} textAnchor="middle" fontSize="11" fill={col} fontWeight="700">{labels[i]}</text>
                <circle cx={toX(r)} cy={oy} r={6} fill={col} stroke="#fff" strokeWidth="2"/>
                <text x={toX(r)} y={oy+18} textAnchor="middle" fontSize="10" fill={col} fontWeight="700">{"x = "+r}</text>
              </g>
            );
          })}
          <rect x={10} y={14} width={230} height={42} rx={4} fill="#fff" stroke="#ccd9ea"/>
          <text x={115} y={29} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">Factored:  (x+2)(x-1)(x-3)</text>
          <text x={115} y={46} textAnchor="middle" fontSize="11" fill="#555" fontFamily="'Courier New',monospace">Expanded: x^3 - 2x^2 - 5x + 6</text>
          <rect x={10} y={H-46} width={W-20} height={38} rx={4} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
          <text x={W/2} y={H-29} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">Each factor (x - r) = 0 when x = r — that root is where the graph crosses the x-axis</text>
          <text x={W/2} y={H-14} textAnchor="middle" fontSize="10" fill="#555">Multiplicity 1: crosses straight through.  Multiplicity 2: touches and bounces.  Multiplicity 3: inflects through.</text>
        </svg>
      </div>
      <div style={S.dCaption}>Every factor (x - r) contributes exactly one root at x = r. Reading a factored polynomial tells you all x-intercepts immediately. The crossing behaviour at each root reveals its multiplicity.</div>
    </div>
  );
}

function DiagramMEApplications() {
  const cW = 160, cH = 150;
  const pad = { l: 32, r: 10, t: 16, b: 28 };
  const iW = cW - pad.l - pad.r, iH = cH - pad.t - pad.b;

  const apps = [
    { title:'Polynomial Curve Fit', subtitle:'Nonlinear stress-strain region', color:'#2E75B6',
      fn: x => 1.5*x - 0.8*x*x + 0.15*x*x*x, xMax:4, yMin:0, yMax:3.5, xLabel:'eps', yLabel:'sigma',
      note:'degree 3 fit to material data',
      data:[[0,0],[0.5,0.68],[1,1.05],[1.8,1.52],[2.5,1.79],[3.2,1.91],[3.8,1.85]] },
    { title:'Transfer Function', subtitle:'H(s) = K / (s^2 + 2zws + w^2)', color:'#c0392b',
      fn: x => x > 0.01 ? 1 / (1 + Math.pow(x/1.5,2)) : 1, xMax:5, yMin:0, yMax:1.1, xLabel:'w', yLabel:'|H|',
      note:'rational frequency response', data:null },
    { title:'Beam Deflection', subtitle:'v(x) = (w/24EI)(x^4 - 2Lx^3 + L^3 x)', color:'#27ae60',
      fn: x => (x*(4-x)*(4-x)*x)/18, xMax:4, yMin:0, yMax:2.2, xLabel:'x', yLabel:'delta',
      note:'degree-4 polynomial shape', data:null },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Variable Functions in Mechanical Engineering</div>
      <div style={{ display:'flex', gap:'14px', flexWrap:'wrap', justifyContent:'center' }}>
        {apps.map(({ title, subtitle, color, fn, xMax, yMin, yMax, xLabel, yLabel, note, data }, ci) => {
          const toX = v => pad.l + (v/xMax)*iW;
          const toY = v => pad.t + iH - ((v-yMin)/(yMax-yMin))*iH;
          const pts = [];
          for (let xi = 0; xi <= xMax; xi += xMax/80) {
            const y = fn(xi);
            if (y >= yMin && y <= yMax*1.05) pts.push(toX(xi).toFixed(1)+','+toY(Math.min(y,yMax)).toFixed(1));
          }
          const path = pts.length > 1 ? 'M '+pts.join(' L ') : '';
          return (
            <div key={ci} style={{ flex:'1', minWidth:'148px', background:'#fff', border:'1px solid #ccd9ea', borderRadius:'6px', padding:'10px', textAlign:'center' }}>
              <div style={{ fontWeight:'700', fontSize:'0.78rem', color, marginBottom:'2px' }}>{title}</div>
              <div style={{ fontFamily:"'Courier New',monospace", fontSize:'0.68rem', color:'#888', marginBottom:'4px' }}>{subtitle}</div>
              <svg width={cW} height={cH} viewBox={"0 0 "+cW+" "+cH} style={{ maxWidth:'100%' }}>
                <line x1={pad.l} y1={pad.t} x2={pad.l} y2={pad.t+iH} stroke="#bbb" strokeWidth="1.2"/>
                <line x1={pad.l} y1={pad.t+iH} x2={pad.l+iW} y2={pad.t+iH} stroke="#bbb" strokeWidth="1.2"/>
                <text x={pad.l+iW+2} y={pad.t+iH+4} fontSize="8" fill="#aaa">{xLabel}</text>
                <text x={pad.l-3} y={pad.t+4} textAnchor="end" fontSize="8" fill="#aaa">{yLabel}</text>
                <path d={path} fill="none" stroke={color} strokeWidth="2.2" strokeLinejoin="round"/>
                <path d={path+" L "+toX(xMax)+","+toY(yMin)+" L "+pad.l+","+toY(yMin)+" Z"} fill={color} fillOpacity="0.08"/>
                {data && data.map(([x,y],i) => <circle key={i} cx={toX(x)} cy={toY(y)} r={3} fill={color} opacity="0.7"/>)}
              </svg>
              <div style={{ fontSize:'0.72rem', color:'#666', fontStyle:'italic', marginTop:'2px' }}>{note}</div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop:'14px', background:'#EBF3FB', border:'1px solid #2E75B6', borderRadius:'6px', padding:'14px' }}>
        <div style={{ fontWeight:'700', fontSize:'0.9rem', color:'#1F3864', marginBottom:'8px', fontFamily:"'Courier New',monospace" }}>Where Polynomial and Rational Functions Appear in ME</div>
        {[
          ['Beam deflection','v(x) — degree 4 polynomial, roots at support boundary conditions'],
          ['Stress-strain (plastic)','Polynomial fit beyond elastic region where Hookes law breaks down'],
          ['Control systems','Transfer functions H(s) = N(s)/D(s) — rational functions in the s-domain'],
          ['Vibration modes','Mode shapes are spatial polynomial functions along the structure length'],
          ['Fluid velocity profiles','Parabolic in laminar pipe flow: v(r) = V_max(1 - r^2/R^2)'],
          ['Complex eigenvalues','Underdamped roots a +/- bi — imaginary part is the damped natural frequency'],
        ].map(([label,desc],i) => (
          <div key={i} style={{ display:'flex', gap:'12px', padding:'5px 0', borderBottom:i<5?'1px solid #d0e4f7':'none' }}>
            <div style={{ fontFamily:"'Courier New',monospace", fontSize:'0.84rem', color:'#2E75B6', fontWeight:'600', minWidth:'190px' }}>{label}</div>
            <div style={{ fontSize:'0.84rem', color:'#555' }}>{desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function VariableFunctions(props) {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Variable Functions</h1>
      <p style={S.p}>Variable functions extend beyond simple lines and parabolas to any expression built from powers, products, and quotients of a variable. Understanding their structure — degree, roots, symmetry, and asymptotes — lets you read a graph and immediately know the underlying equation type, and predict behaviour without plotting every point.</p>

      <InfoBox
        title="General Polynomial:  f(x) = an*x^n + a(n-1)*x^(n-1) + ... + a1*x + a0"
        items={[
          "n = degree — the highest exponent. Sets the maximum number of roots and turning points.",
          "an = leading coefficient — controls end behaviour and whether the curve opens up or down.",
          "Roots (zeros) are x-values where f(x) = 0 — equivalently, where it crosses the x-axis.",
          "A degree-n polynomial has at most n real roots and at most n-1 turning points.",
        ]}
      />

      <h2 style={S.h2}>Polynomial Anatomy</h2>
      <DiagramPolynomialAnatomy />
      <Table2 rows={[
        ["Degree",          "Highest power of x\nDetermines max roots and turning points"],
        ["Leading term",    "an*x^n — dominates for large |x|, sets end behaviour"],
        ["End behaviour",   "Odd degree, positive leading: falls left, rises right\nOdd degree, negative leading: rises left, falls right\nEven degree, positive leading: rises both sides\nEven degree, negative leading: falls both sides"],
        ["Turning points",  "Local max or min — where f'(x) = 0\nMax turning points = degree - 1"],
        ["Y-intercept",     "Always = a0 (constant term) — set x = 0"],
        ["Multiplicity",    "Factor (x-r)^m:\nm=1: crosses through\nm=2: touches and bounces\nm=3: inflects through"],
      ]} />

      <h2 style={S.h2}>Even and Odd Functions</h2>
      <DiagramEvenOdd />
      <Table2 rows={[
        ["Even function",   "f(-x) = f(x) — symmetric about y-axis\nExamples: x^2, x^4, cos(x), |x|"],
        ["Odd function",    "f(-x) = -f(x) — rotational symmetry about origin\nExamples: x, x^3, sin(x), tan(x)"],
        ["Neither",         "Most functions — not symmetric about y-axis or origin\nExample: f(x) = x^2 + x"],
        ["Testing",         "Replace x with -x and simplify:\nResult = f(x) → even\nResult = -f(x) → odd\nOtherwise → neither"],
        ["Why it matters",  "Even: integral from -a to a = 2 * integral from 0 to a\nOdd: integral from -a to a = 0 (saves huge work in Fourier analysis)"],
      ]} />

      <h2 style={S.h2}>Complex Numbers</h2>
      <DiagramComplexPlane />
      <Table2 rows={[
        ["Definition",      "z = a + bi  where  i = sqrt(-1)\na = real part,  b = imaginary part"],
        ["i powers",        "i^1 = i,  i^2 = -1,  i^3 = -i,  i^4 = 1  (repeating cycle)"],
        ["Magnitude",       "|z| = sqrt(a^2 + b^2) — length of vector from origin"],
        ["Argument",        "theta = arctan(b/a) — angle from positive real axis"],
        ["Conjugate",       "z* = a - bi — reflects across real axis\nz * z* = a^2 + b^2 = |z|^2"],
        ["Polar / Euler",   "z = r(cos t + i sin t) = r*e^(it)\nUsed in vibrations, AC circuits, Laplace transforms"],
        ["Multiplication",  "(a+bi)(c+di) = (ac-bd) + (ad+bc)i\nIn polar: multiply magnitudes, add angles"],
        ["ME significance", "Complex roots of characteristic equation = underdamped oscillation\nImaginary part = damped frequency, real part = decay rate"],
      ]} />

      <h2 style={S.h2}>Rational Expressions</h2>
      <DiagramRational />
      <Table2 rows={[
        ["Definition",          "f(x) = P(x)/Q(x) — ratio of two polynomials\nDomain excludes all x where Q(x) = 0"],
        ["Vertical asymptote",  "x = a where Q(a)=0 but P(a) is not 0\nCurve shoots to +/- infinity near this line"],
        ["Hole (removable)",    "x = a where both P(a)=0 and Q(a)=0\nFactor cancels — plot an open hole"],
        ["Horizontal asymptote","deg P < deg Q:  y = 0\ndeg P = deg Q:  y = ratio of leading coefficients\ndeg P > deg Q:  no HA, oblique asymptote"],
        ["Zeros",               "Where P(x) = 0 (and Q is not zero there)"],
        ["ME use",              "Transfer functions H(s), impedance Z(s), frequency response — all rational"],
      ]} />

      <h2 style={S.h2}>Factoring and Roots</h2>
      <DiagramFactoring />
      <Table2 rows={[
        ["Factor theorem",      "If f(r) = 0 then (x-r) is a factor of f(x)"],
        ["Fundamental theorem", "Degree-n polynomial has exactly n roots in the complex numbers (counting multiplicity)"],
        ["Rational root test",  "Possible rational roots = +/-(factors of a0) / (factors of an)"],
        ["Synthetic division",  "Efficient division by (x-r) — remainder of 0 confirms root"],
        ["Complex roots",       "Always come in conjugate pairs for real-coefficient polynomials\nIf 2+3i is a root, so is 2-3i"],
        ["Graph to factors",    "Read x-intercepts, write (x-root) factors\nCrossing style reveals multiplicity"],
      ]} />

      <h2 style={S.h2}>Mechanical Engineering Applications</h2>
      <DiagramMEApplications />

      <InfoBox
        title="Key Takeaway — Degree Tells You the Physics"
        items={[
          "Degree 1 (linear): constant rate — spring force, Ohm's law, thermal expansion.",
          "Degree 2 (quadratic): parabolic — kinetic energy, projectile height, beam moment under point load.",
          "Degree 3 (cubic): inflection — beam slope, plastic stress-strain, S-curves in material behaviour.",
          "Degree 4 (quartic): two inflections — beam deflection under distributed load (standard result in mechanics of materials).",
          "Rational (P/Q): asymptotic behaviour — transfer functions, impedance, resonance peaks in frequency response.",
          "Complex roots: imaginary part is oscillation frequency, real part is growth or decay rate.",
        ]}
      />
    </div>
  );
}